import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const T = {
  ink: "#17131F", accent: "var(--accent)", slate: "#7C7590",
  border: "#E8E4DC", white: "#FFFFFF", paper: "#FBFAFE",
  danger: "#B91C1C", good: "#1A7A4A",
};

interface Notification {
  id: string;
  type: "auto" | "todo";
  message: string;
  link?: string;
  created_at: string;
}

interface Props {
  mode?: "recruiter" | "candidate";
}

export function NotificationBell({ mode = "recruiter" }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [newTodo, setNewTodo] = useState("");
  const [adding, setAdding] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const apiBase = mode === "candidate" ? "/candidate/notifications" : "/recruiter/notifications";

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get<{ ok: boolean; notifications: Notification[] }>(apiBase);
      if (res.ok) setItems(res.notifications);
    } catch {}
  }, [apiBase]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  async function dismiss(id: string) {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`${apiBase}/${id}`); } catch {}
  }

  async function dismissAll() {
    setItems([]);
    try { await api.delete(`${apiBase}/all`); } catch {}
  }

  async function addTodo(e: React.FormEvent) {
    e.preventDefault();
    const msg = newTodo.trim();
    if (!msg) return;
    setAdding(true);
    try {
      const res = await api.post<{ ok: boolean; notification: Notification }>(apiBase, { message: msg });
      if (res.ok) {
        setItems((prev) => [res.notification, ...prev]);
        setNewTodo("");
      }
    } catch {}
    setAdding(false);
    inputRef.current?.focus();
  }

  function handleItemClick(item: Notification) {
    if (item.link) {
      setOpen(false);
      navigate(item.link);
    }
  }

  const count = items.length;

  return (
    <div ref={dropRef} style={{ position: "relative" }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${count ? ` (${count})` : ""}`}
        style={{
          position: "relative", background: "none", border: "none",
          cursor: "pointer", padding: "6px 8px", borderRadius: 8,
          color: open ? T.accent : T.slate,
          transition: "color 0.15s",
        }}
      >
        <BellIcon />
        {count > 0 && (
          <span style={{
            position: "absolute", top: 2, right: 2,
            minWidth: 16, height: 16, borderRadius: 8,
            background: T.accent, color: T.white,
            fontSize: 10, fontWeight: 700, lineHeight: "16px",
            textAlign: "center", padding: "0 4px",
            boxSizing: "border-box",
          }}>
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: 340, maxHeight: 480,
          background: T.white, borderRadius: 12,
          boxShadow: "0 24px 64px rgba(27,26,36,0.14), 0 8px 20px rgba(27,26,36,0.07)",
          border: `1px solid ${T.border}`,
          display: "flex", flexDirection: "column",
          zIndex: 1000, overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 16px", borderBottom: `1px solid ${T.border}`,
          }}>
            <span style={{ fontWeight: 700, fontSize: 13.5, color: T.ink }}>
              Notifications {count > 0 && <span style={{ color: T.slate, fontWeight: 400 }}>({count})</span>}
            </span>
            {count > 0 && (
              <button onClick={dismissAll} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12.5, color: T.slate, padding: 0,
                textDecoration: "underline", textUnderlineOffset: 2,
              }}>
                Clear all
              </button>
            )}
          </div>

          {/* Add to-do — recruiters only */}
          {mode === "recruiter" && <form onSubmit={addTodo} style={{
            display: "flex", gap: 8, padding: "10px 12px",
            borderBottom: `1px solid ${T.border}`,
          }}>
            <input
              ref={inputRef}
              type="text"
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              placeholder="Add a to-do…"
              style={{
                flex: 1, padding: "7px 10px", fontSize: 13,
                border: `1px solid ${T.border}`, borderRadius: 6,
                outline: "none", color: T.ink, background: T.paper,
              }}
            />
            <button type="submit" disabled={adding || !newTodo.trim()} style={{
              padding: "7px 12px", fontSize: 12.5, fontWeight: 600,
              color: T.white, background: T.accent, border: "none",
              borderRadius: 6, cursor: adding || !newTodo.trim() ? "default" : "pointer",
              opacity: adding || !newTodo.trim() ? 0.5 : 1,
            }}>
              Add
            </button>
          </form>}

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {items.length === 0 ? (
              <div style={{
                padding: "32px 16px", textAlign: "center",
                color: T.slate, fontSize: 13.5,
              }}>
                You're all caught up ✓
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "10px 12px",
                    borderBottom: `1px solid ${T.border}`,
                    cursor: item.link ? "pointer" : "default",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => { if (item.link) (e.currentTarget as HTMLDivElement).style.background = "#F5F3FF"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = ""; }}
                  onClick={() => handleItemClick(item)}
                >
                  {/* Type dot */}
                  <span style={{
                    width: 7, height: 7, borderRadius: 4, flexShrink: 0, marginTop: 5,
                    background: item.type === "auto" ? T.accent : T.slate,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0, fontSize: 13, color: T.ink,
                      lineHeight: 1.4, wordBreak: "break-word",
                    }}>
                      {item.message}
                    </p>
                    <p style={{ margin: "3px 0 0", fontSize: 11.5, color: T.slate }}>
                      {_relativeTime(item.created_at)}
                      {item.type === "todo" && (
                        <span style={{ marginLeft: 6, opacity: 0.6 }}>· to-do</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismiss(item.id); }}
                    title="Dismiss"
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: T.slate, fontSize: 14, padding: "2px 4px",
                      borderRadius: 4, lineHeight: 1, flexShrink: 0,
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = T.danger; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = T.slate; }}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function _relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
